import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.jpeg';

const Register = () => {
  const [searchParams] = useSearchParams();
  const sponsorCodeFromUrl = searchParams.get('sponsor') || searchParams.get('sponserid');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    sponsorCode: sponsorCodeFromUrl || ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [sponsorValid, setSponsorValid] = useState(null);
  const [checkingSponsor, setCheckingSponsor] = useState(false);

  const navigate = useNavigate();
  const { register, checkSponsorCode } = useAuth();

  // Check sponsor code validity when user types
  useEffect(() => {
    const validateSponsor = async () => {
      const code = formData.sponsorCode;
      if (code && code.length >= 5) {
        setCheckingSponsor(true);
        const result = await checkSponsorCode(code);
        console.log(result)
        
        if (result.requiresPurchase) {
          // Sponsor exists but hasn't made purchase
          setSponsorValid(false);
          setErrors(prev => ({
            ...prev,
            sponsorCode: 'Sponsor must make at least one purchase before referring new members!'
          }));
        } else if (result.valid) {
          // Sponsor is valid and has purchase
          setSponsorValid(result.sponsor);
          setErrors(prev => ({ ...prev, sponsorCode: '' }));
        } else {
          // Invalid sponsor code
          setSponsorValid(null);
          if (touched.sponsorCode) {
            setErrors(prev => ({
              ...prev,
              sponsorCode: 'Invalid sponsor code'
            }));
          }
        }
        
        setCheckingSponsor(false);
      } else {
        setSponsorValid(null);
      }
    };

    const timeoutId = setTimeout(validateSponsor, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.sponsorCode, checkSponsorCode, touched.sponsorCode]);

  // Validate single field
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return '';

      case 'email':
        if (!value) return 'Email is required';
        if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value)) {
          return 'Please enter a valid email';
        }
        return '';

      case 'phone':
        if (!value) return 'Phone number is required';
        if (!/^[6-9]\d{9}$/.test(value)) {
          return 'Please enter a valid 10-digit Indian mobile number';
        }
        return '';

      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return '';

      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return '';

      case 'sponsorCode':
        if (!value) return 'SPONSOR CODE IS REQUIRED - You must have a sponsor to join!';
        if (value.length < 5) return 'Sponsor code should be at least 5 characters';
        return '';

      default:
        return '';
    }
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    // Also check if sponsor is valid
    if (!sponsorValid && formData.sponsorCode) {
      newErrors.sponsorCode = 'Invalid sponsor code';
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched(prev => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach(key => allTouched[key] = true);
    setTouched(allTouched);

    // Validate all fields
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error
      const firstErrorField = document.querySelector('[data-error="true"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Final sponsor validation
    if (!sponsorValid) {
      setErrors(prev => ({
        ...prev,
        sponsorCode: 'Please enter a valid sponsor code'
      }));
      return;
    }

    setSubmitError('');
    setLoading(true);

    try {
      const result = await register(
        formData.name.trim(),
        formData.email.toLowerCase().trim(),
        formData.phone.trim(),
        formData.password,
        formData.sponsorCode.trim()
      );

      if (result.success) {
        alert(result.message);

        setShowSuccess(true);
        // Show success message then redirect
        setTimeout(() => {
          // navigate('/login');
          window.location.href = '/login';
        }, 100);
      } else {
        setSubmitError(result.error || 'Registration failed. Please try again.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setSubmitError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Success Modal
  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F2F3D] to-[#091E28] px-5">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-[#0F2F3D] mb-4">Registration Successful!</h2>
          <p className="text-gray-600 mb-2">Welcome to Our Family!</p>
          <p className="text-sm text-gray-500 mb-6">Redirecting to dashboard...</p>
          <div className="animate-pulse text-[#E4572E]">Loading</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F2F3D] to-[#091E28] px-5 py-10">
      <div className="w-full max-w-md bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-8 relative overflow-hidden">

        {/* Top Accent Bar */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#E4572E] to-[#F47C4D]"></div>

        {/* Logo + Heading */}
        <div className="flex flex-col items-center mb-4">
          <img src={logo} alt="GO WEALTH" className="w-20 h-20 object-contain mb-2" />
          <h1 className="text-2xl font-bold text-[#0F2F3D] tracking-wide">
            Direct Selling Made Easy
          </h1>
          <p className="text-sm text-gray-500">
            Start Your Wealth Journey Today
          </p>
        </div>


        {/* Sponsor Code from URL */}
        {(sponsorCodeFromUrl) && (
          <div className="mb-4 p-3 bg-green-50 border border-green-300 rounded-lg">
            <p className="text-sm text-green-700 text-center font-medium">
              🔥 Referral Code Detected: <span className="font-mono font-bold">{sponsorCodeFromUrl}</span>
            </p>
          </div>
        )}

        {/* Sponsor Info when valid */}
        {sponsorValid && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-300 rounded-lg">
            <p className="text-sm text-blue-700">
              ✓ Valid Sponsor: <span className="font-bold">{sponsorValid.name}</span>
              <span className="ml-2 text-xs">({sponsorValid.mySponsorCode})</span>
              {sponsorValid.hasPurchase && (
                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  ✓ Active Sponsor
                </span>
              )}
            </p>
          </div>
        )}

        {/* Sponsor hasn't made purchase */}
        {sponsorValid === false && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-300 rounded-lg">
            <p className="text-sm text-orange-700">
              ⚠️ Sponsor exists but hasn't made any purchase yet.
            </p>
            <p className="text-xs text-orange-600 mt-1">
              Ask your sponsor to make a purchase first before they can refer new members.
            </p>
          </div>
        )}

        {/* Help Text - Don't have sponsor? */}
        {!formData.sponsorCode && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-700 text-center">
              Don't have a sponsor code? Contact any existing member to get one.
            </p>
          </div>
        )}

        {/* Error Message */}
        {submitError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 rounded-lg">
            <p className="text-sm text-red-600 font-medium">{submitError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3" noValidate>

          {/* Name Field */}
          <div>
            <label className="block mb-1 text-sm font-semibold text-[#0F2F3D]">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter your full name"
              data-error={touched.name && !!errors.name}
              className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${touched.name && errors.name
                  ? 'border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-[#E4572E] focus:ring-[#E4572E]/20'
                }`}
            />
            {touched.name && errors.name && (
              <p className="mt-1 text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label className="block mb-1 text-sm font-semibold text-[#0F2F3D]">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter your email"
              data-error={touched.email && !!errors.email}
              className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${touched.email && errors.email
                  ? 'border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-[#E4572E] focus:ring-[#E4572E]/20'
                }`}
            />
            {touched.email && errors.email && (
              <p className="mt-1 text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <label className="block mb-1 text-sm font-semibold text-[#0F2F3D]">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="10-digit mobile number"
              maxLength="10"
              data-error={touched.phone && !!errors.phone}
              className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${touched.phone && errors.phone
                  ? 'border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-[#E4572E] focus:ring-[#E4572E]/20'
                }`}
            />
            {touched.phone && errors.phone && (
              <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
            )}
          </div>

          {/* 🔥 SPONSOR CODE FIELD - HIGHLIGHTED */}
          <div className="relative">
            <label className="block mb-1 text-sm font-semibold text-[#0F2F3D]">
              Sponsor Code <span className="text-red-500">*</span>
              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                Required
              </span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="sponsorCode"
                value={formData.sponsorCode}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter Sponsor Code (e.g., DMC12345)"
                disabled={checkingSponsor}
                className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all font-mono ${checkingSponsor ? 'bg-gray-100' : ''
                  } ${touched.sponsorCode && errors.sponsorCode
                    ? 'border-red-500 focus:ring-red-200 bg-red-50'
                    : sponsorValid
                      ? 'border-green-500 bg-green-50 focus:ring-green-200'
                      : formData.sponsorCode
                        ? 'border-yellow-400 bg-yellow-50 focus:ring-yellow-200'
                        : 'border-gray-300'
                  }`}
                style={{ textTransform: 'uppercase' }}
              />
              {checkingSponsor && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#E4572E]"></div>
                </div>
              )}
              {sponsorValid && !checkingSponsor && (
                <div className="absolute right-3 top-3 text-green-500">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            {touched.sponsorCode && errors.sponsorCode ? (
              <p className="mt-1 text-xs text-red-500 font-medium">{errors.sponsorCode}</p>
            ) : !formData.sponsorCode && (
              <p className="mt-1 text-xs text-yellow-600">
                ⚠️ You must have a valid sponsor code to join this system
              </p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label className="block mb-1 text-sm font-semibold text-[#0F2F3D]">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Minimum 6 characters"
              data-error={touched.password && !!errors.password}
              className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${touched.password && errors.password
                  ? 'border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-[#E4572E] focus:ring-[#E4572E]/20'
                }`}
            />
            {touched.password && errors.password && (
              <p className="mt-1 text-xs text-red-500">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label className="block mb-1 text-sm font-semibold text-[#0F2F3D]">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Re-enter password"
              data-error={touched.confirmPassword && !!errors.confirmPassword}
              className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${touched.confirmPassword && errors.confirmPassword
                  ? 'border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-[#E4572E] focus:ring-[#E4572E]/20'
                }`}
            />
            {touched.confirmPassword && errors.confirmPassword && (
              <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || checkingSponsor || !sponsorValid}
            className={`w-full py-3 rounded-lg bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white font-semibold shadow-md transition-all duration-300 mt-4 ${loading || checkingSponsor || !sponsorValid
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:opacity-90 hover:shadow-lg'
              }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Registering...
              </span>
            ) : (
              'Join US Now'
            )}
          </button>

        </form>

        {/* Login Link */}
        <p className="text-center mt-4 text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-[#E4572E] font-semibold hover:underline">
            Login Here
          </Link>
        </p>

        {/* Terms */}
        <p className="text-center mt-3 text-xs text-gray-400">
          By joining, you agree to our Terms & Conditions
        </p>
      </div>
    </div>
  );
};

export default Register;